// Feb 2026 — State-preserving tab shell for the admin surface.
//
// Wraps every `/admin/*` path defined in `config/adminRoutes.js` into
// a tabbed workspace: opening a page adds it to a strip pinned at the
// top; switching tabs shows the previously-mounted page instantly
// (its filters, selections and scroll position intact). Non-active
// tabs stay mounted but `display: none` so they don't render.
//
// Constraints & trade-offs:
//   * Only static admin paths are tabbable (see `ADMIN_ROUTE_MAP`).
//     Unknown paths (e.g. a future `/admin/orders/:id`) render as a
//     plain React child without keep-alive.
//   * `useLocation()` inside a tab reflects the BROWSER url, which is
//     always the active tab's path. Hidden tabs therefore see the
//     active tab's location — acceptable for admin pages that use
//     API state, not URL state.
//   * Persists to localStorage under `admin_tabs_v1`. Corrupt entries
//     are silently discarded on load.
//
// The Dashboard tab (`/admin`) is pinned and cannot be closed so the
// shell always has an anchor to fall back to.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminTabsBar, { loadPersistedTabs, persistTabs } from './AdminTabsBar';
import { ADMIN_ROUTE_MAP, resolveAdminRoute } from '../config/adminRoutes';

const DASHBOARD_KEY = '/admin';

const seedTabs = () => {
  const persisted = loadPersistedTabs();
  if (persisted && persisted.tabs.length) {
    // Filter out entries that no longer exist in the route map (e.g.
    // a page was removed between deploys).
    const valid = persisted.tabs.filter((t) => ADMIN_ROUTE_MAP[t.key]);
    const hasDash = valid.some((t) => t.key === DASHBOARD_KEY);
    if (!hasDash) {
      valid.unshift({
        key: DASHBOARD_KEY,
        path: DASHBOARD_KEY,
        title: ADMIN_ROUTE_MAP[DASHBOARD_KEY].title,
        pinned: true,
      });
    }
    return {
      tabs: valid,
      activeKey: valid.some((t) => t.key === persisted.activeKey)
        ? persisted.activeKey
        : DASHBOARD_KEY,
    };
  }
  return {
    tabs: [{ key: DASHBOARD_KEY, path: DASHBOARD_KEY, title: 'Dashboard', pinned: true }],
    activeKey: DASHBOARD_KEY,
  };
};

const AdminTabsShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [{ tabs, activeKey }, setState] = useState(seedTabs);
  // Guard so the URL-sync effect doesn't infinite-loop with the
  // navigate() calls it makes when a stale tab is activated.
  const syncedPathRef = useRef(null);

  // Persist on every change.
  useEffect(() => {
    persistTabs(tabs, activeKey);
  }, [tabs, activeKey]);

  // Sync tabs with the browser URL. Any navigation to a tabbable
  // admin path either activates an existing tab or spawns a new one.
  useEffect(() => {
    const raw = location.pathname.replace(/\/+$/, '') || '/admin';
    if (raw === syncedPathRef.current) return;
    syncedPathRef.current = raw;
    const route = resolveAdminRoute(raw);
    if (!route) return; // unknown admin URL — leave tabs untouched.
    setState((prev) => {
      const existing = prev.tabs.find((t) => t.key === raw);
      if (existing) {
        return prev.activeKey === raw ? prev : { ...prev, activeKey: raw };
      }
      // Cap at 12 tabs (per spec) — when the ceiling is hit, close
      // the OLDEST non-pinned tab to make room for the new one.
      const MAX_TABS = 12;
      let nextTabs = [...prev.tabs, { key: raw, path: raw, title: route.title }];
      if (nextTabs.length > MAX_TABS) {
        const firstUnpinned = nextTabs.findIndex((t) => !t.pinned);
        if (firstUnpinned >= 0) nextTabs.splice(firstUnpinned, 1);
      }
      return { tabs: nextTabs, activeKey: raw };
    });
  }, [location.pathname]);

  const switchTo = useCallback((tab) => {
    if (tab.key === activeKey) return;
    navigate(tab.path);
  }, [navigate, activeKey]);

  const closeTab = useCallback((tab) => {
    if (tab.pinned) return;
    setState((prev) => {
      const idx = prev.tabs.findIndex((t) => t.key === tab.key);
      if (idx < 0) return prev;
      const nextTabs = prev.tabs.filter((t) => t.key !== tab.key);
      let nextActive = prev.activeKey;
      if (prev.activeKey === tab.key) {
        // Fall back to the neighbour on the left, then the right,
        // then the dashboard anchor.
        const fallback =
          prev.tabs[idx - 1] ||
          prev.tabs[idx + 1] ||
          nextTabs[0];
        nextActive = fallback ? fallback.key : DASHBOARD_KEY;
        // Push the URL asynchronously so React finishes the state
        // update before react-router fires its useLocation watcher.
        setTimeout(() => navigate(nextActive), 0);
      }
      return { tabs: nextTabs, activeKey: nextActive };
    });
  }, [navigate]);

  const closeOthers = useCallback((keepTab) => {
    setState((prev) => {
      const nextTabs = prev.tabs.filter((t) => t.pinned || t.key === keepTab.key);
      const activeStays = nextTabs.some((t) => t.key === prev.activeKey);
      const nextActive = activeStays ? prev.activeKey : keepTab.key;
      if (!activeStays) setTimeout(() => navigate(keepTab.path), 0);
      return { tabs: nextTabs, activeKey: nextActive };
    });
  }, [navigate]);

  const closeAll = useCallback(() => {
    setState((prev) => {
      const nextTabs = prev.tabs.filter((t) => t.pinned);
      const nextActive = nextTabs[0]?.key || DASHBOARD_KEY;
      setTimeout(() => navigate(nextActive), 0);
      return { tabs: nextTabs, activeKey: nextActive };
    });
  }, [navigate]);

  // Memoise the rendered stack so each tab component only mounts
  // once per tab key. Switching tabs is then a pure `display` toggle.
  const stack = useMemo(() => {
    return tabs.map((tab) => {
      const route = ADMIN_ROUTE_MAP[tab.key];
      if (!route) return null;
      const Component = route.Component;
      const visible = tab.key === activeKey;
      return (
        <div
          key={tab.key}
          data-testid={`admin-tab-panel-${tab.key.replace(/\W+/g, '-')}`}
          style={{ display: visible ? 'block' : 'none' }}
          aria-hidden={!visible}
        >
          <Component />
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, activeKey]);

  // Guard: if the current URL doesn't map to a tabbable admin route
  // (e.g. a page not registered in ADMIN_ROUTE_MAP), fall back to
  // rendering nothing — the outer <Routes> will show its 404 path.
  const currentRoute = resolveAdminRoute(location.pathname);
  if (!currentRoute) {
    return null;
  }

  return (
    <>
      <AdminTabsBar
        tabs={tabs}
        activeKey={activeKey}
        onSwitch={switchTo}
        onClose={closeTab}
        onCloseOthers={closeOthers}
        onCloseAll={closeAll}
      />
      {stack}
    </>
  );
};

export default AdminTabsShell;
